import { ref, computed } from 'vue'

/**
 * Composable for parsing @project and #tag tokens from omnibox input.
 * Manages committed projects/tags as pills and extracts a clean title.
 */
export function useOmniboxParser(store) {
  const inputValue = ref('')
  const committedProject = ref(null) // { id, name } or { name } for new
  const committedTags = ref([])      // [{ id, name }, ...]
  const activeToken = ref(null)      // { type: 'project'|'tag', startIndex: number }
  const selectedAutocompleteIndex = ref(0)
  const inheritedFromContext = ref(false)

  const hasPills = computed(() => committedProject.value || committedTags.value.length > 0)

  /**
   * Extract the current token being typed (after @ or #)
   */
  const currentTokenText = computed(() => {
    if (!activeToken.value) return ''
    const text = inputValue.value.slice(activeToken.value.startIndex)
    return text
  })

  /**
   * Filter items for autocomplete based on current token text
   */
  const filteredProjects = computed(() => {
    const query = currentTokenText.value.toLowerCase()
    return store.projects.filter(p => p.name.includes(query))
  })

  const filteredTags = computed(() => {
    const query = currentTokenText.value.toLowerCase()
    const committedNames = new Set(committedTags.value.map(t => t.name))
    return store.tags.filter(t => t.name.includes(query) && !committedNames.has(t.name))
  })

  const showAutocomplete = computed(() => !!activeToken.value)
  const autocompleteType = computed(() => activeToken.value?.type || null)

  const autocompleteItems = computed(() => {
    if (!activeToken.value) return []
    return activeToken.value.type === 'project' ? filteredProjects.value : filteredTags.value
  })

  /**
   * Whether to show "Create new" option
   */
  const showCreateOption = computed(() => {
    if (!activeToken.value || !currentTokenText.value) return false
    const query = currentTokenText.value.toLowerCase()
    const items = autocompleteItems.value
    return query.length > 0 && !items.some(i => i.name === query)
  })

  /**
   * Handle keydown on the input
   */
  function onKeydown(e) {
    if (showAutocomplete.value) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const maxIdx = autocompleteItems.value.length + (showCreateOption.value ? 0 : -1)
        selectedAutocompleteIndex.value = Math.min(selectedAutocompleteIndex.value + 1, maxIdx)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        selectedAutocompleteIndex.value = Math.max(selectedAutocompleteIndex.value - 1, 0)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        cancelToken({ removeFromInput: true })
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (selectedAutocompleteIndex.value < autocompleteItems.value.length) {
          selectItem(autocompleteItems.value[selectedAutocompleteIndex.value])
        } else if (showCreateOption.value) {
          createFromToken()
        } else {
          // No items, no create — commit as submit
          cancelToken({ removeFromInput: true })
          return // Let the form handle Enter
        }
        return
      }
      if (e.key === ' ') {
        if (currentTokenText.value.length > 0) {
          e.preventDefault()
          createFromToken()
          return
        }
      }
    }
  }

  /**
   * Handle input changes — detect @ and # triggers
   */
  function onInput() {
    if (activeToken.value) {
      // Check if user deleted back past the trigger
      const textSinceTrigger = inputValue.value.slice(activeToken.value.startIndex)
      if (textSinceTrigger.includes(' ')) {
        cancelToken({ removeFromInput: true })
      } else if (inputValue.value.length < activeToken.value.startIndex) {
        cancelToken()
      }
      selectedAutocompleteIndex.value = 0
      return
    }

    // Detect new trigger
    const val = inputValue.value
    const lastChar = val[val.length - 1]
    const charBefore = val.length > 1 ? val[val.length - 2] : ' '

    if (lastChar === '@' && (charBefore === ' ' || val.length === 1)) {
      if (!committedProject.value) {
        activeToken.value = { type: 'project', startIndex: val.length }
        selectedAutocompleteIndex.value = 0
      }
    } else if (lastChar === '#' && (charBefore === ' ' || val.length === 1)) {
      activeToken.value = { type: 'tag', startIndex: val.length }
      selectedAutocompleteIndex.value = 0
    }
  }

  /**
   * Select an existing item from autocomplete
   */
  function selectItem(item) {
    if (!activeToken.value) return
    inheritedFromContext.value = false

    if (activeToken.value.type === 'project') {
      committedProject.value = item
    } else {
      if (!committedTags.value.some(t => t.name === item.name)) {
        committedTags.value.push(item)
      }
    }

    // Remove the trigger + typed text from input
    const beforeTrigger = inputValue.value.slice(0, activeToken.value.startIndex - 1).trimEnd()
    inputValue.value = beforeTrigger ? beforeTrigger + ' ' : ''
    activeToken.value = null
    selectedAutocompleteIndex.value = 0
  }

  /**
   * Create a new project/tag from the current token text
   */
  function createFromToken() {
    if (!activeToken.value || !currentTokenText.value) return

    const name = currentTokenText.value.toLowerCase().replace(/\s/g, '-')
    if (!name) return
    inheritedFromContext.value = false

    if (activeToken.value.type === 'project') {
      committedProject.value = { name }
    } else {
      if (!committedTags.value.some(t => t.name === name)) {
        committedTags.value.push({ name })
      }
    }

    const beforeTrigger = inputValue.value.slice(0, activeToken.value.startIndex - 1).trimEnd()
    inputValue.value = beforeTrigger ? beforeTrigger + ' ' : ''
    activeToken.value = null
    selectedAutocompleteIndex.value = 0
  }

  function stripActiveTokenFromInput() {
    if (!activeToken.value) return

    const beforeTrigger = inputValue.value.slice(0, activeToken.value.startIndex - 1).trimEnd()
    inputValue.value = beforeTrigger ? beforeTrigger + ' ' : ''
  }

  function cancelToken(options = {}) {
    if (options.removeFromInput) {
      stripActiveTokenFromInput()
    }

    activeToken.value = null
    selectedAutocompleteIndex.value = 0
  }

  function finalizeActiveToken() {
    if (!activeToken.value) return false

    if (currentTokenText.value.length > 0) {
      createFromToken()
      return true
    }

    cancelToken({ removeFromInput: true })
    return false
  }

  function removeProject() {
    committedProject.value = null
    inheritedFromContext.value = false
  }

  function removeTag(index) {
    committedTags.value.splice(index, 1)
    inheritedFromContext.value = false
  }

  /**
   * Get the payload for creating a timer
   */
  function getPayload() {
    const title = inputValue.value.trim()
    const payload = { title }

    if (committedProject.value) {
      if (committedProject.value.id) {
        payload.projectId = committedProject.value.id
      } else {
        payload.projectName = committedProject.value.name
      }
    }

    const existingTags = committedTags.value.filter(t => t.id)
    const newTags = committedTags.value.filter(t => !t.id)
    if (existingTags.length) payload.tagIds = existingTags.map(t => t.id)
    if (newTags.length) payload.tagNames = newTags.map(t => t.name)

    return payload
  }

  function reset() {
    inputValue.value = ''
    committedProject.value = null
    committedTags.value = []
    activeToken.value = null
    selectedAutocompleteIndex.value = 0
    inheritedFromContext.value = false
  }

  function clearInheritedContext() {
    if (!inheritedFromContext.value) return

    committedProject.value = null
    committedTags.value = []
    inheritedFromContext.value = false
  }

  function prefillFromContext(context) {
    if (!context) return
    committedProject.value = context.project ? { id: context.project.id, name: context.project.name } : null
    committedTags.value = (context.tags || []).map(t => ({ id: t.id, name: t.name }))
    inheritedFromContext.value = !!(committedProject.value || committedTags.value.length)
  }

  return {
    inputValue,
    committedProject,
    committedTags,
    hasPills,
    showAutocomplete,
    autocompleteType,
    autocompleteItems,
    currentTokenText,
    showCreateOption,
    selectedAutocompleteIndex,
    inheritedFromContext,
    onKeydown,
    onInput,
    selectItem,
    createFromToken,
    cancelToken,
    finalizeActiveToken,
    removeProject,
    removeTag,
    getPayload,
    reset,
    clearInheritedContext,
    prefillFromContext,
  }
}
